import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import s from './Toast.module.css';

/** How long a toast stays (design: 2800 ms). */
export const TOAST_MS = 2800;

type Show = (text: string) => void;
const ToastContext = createContext<Show | null>(null);

/** One toast at a time; a new one replaces the current and restarts the timer (as the design does). */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<{ text: string; id: number } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const show = useCallback<Show>((text) => {
    window.clearTimeout(timer.current);
    setToast({ text, id: Date.now() });
    timer.current = window.setTimeout(() => setToast(null), TOAST_MS);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      {toast &&
        createPortal(
          <div key={toast.id} className={s.toast} role="status" aria-live="polite">
            <Icon name="circle-check" size={16} className={s.icon} />
            {toast.text}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

/** Returns `toast(text)`. Every mutation confirms with a toast (DESIGN_SPEC §6). */
export function useToast(): Show {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
